import { useEffect, useId, useRef } from "react";
import { floatingSheet, theme } from "../theme";

// Mirrors the seeded ServiceTier names (backend/prisma/seed.ts) and the tier
// cards on abacassecurity.com/pricing.
export const TIER_NAMES: Record<number, string> = {
  1: "Basic Self-Service",
  2: "Self-Service + Documents",
  3: "Self-Service + AI",
  4: "Consultant-Led",
  5: "Full Service",
};

export interface TierErrorPayload {
  error: string;
  message: string;
  requiredLevel: number;
  currentLevel: number;
}

interface TierLockModalProps {
  featureName: string;
  requiredTierName: string;
  requiredTierLevel: number;
  currentTierLevel: number;
  onClose: () => void;
  onRequestUpgrade: () => void;
}

export function TierLockModal({
  featureName,
  requiredTierName,
  currentTierLevel,
  onClose,
  onRequestUpgrade,
}: TierLockModalProps) {
  const titleId = useId();
  const descId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  // Callers pass inline handlers; keep the latest without re-running the
  // mount effect (which would steal focus back on every parent render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Escape closes; focus moves into the dialog on open and returns to the
  // element that opened it when the dialog goes away.
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onCloseRef.current(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (previous && document.contains(previous)) previous.focus();
    };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        onClick={(e) => e.stopPropagation()}
        style={{
          ...floatingSheet,
          padding: 28,
          maxWidth: 420,
          width: "100%",
          fontFamily: theme.font,
        }}
      >
        <div
          id={titleId}
          style={{
            fontSize: 16,
            fontWeight: 700,
            color: theme.color.heading,
            marginBottom: 12,
          }}
        >
          {featureName} requires a higher tier
        </div>
        <div
          id={descId}
          style={{
            fontSize: 13,
            color: theme.color.text,
            lineHeight: 1.7,
            marginBottom: 22,
          }}
        >
          You are currently on <strong>{TIER_NAMES[currentTierLevel] ?? `tier ${currentTierLevel}`}</strong>. This feature requires{" "}
          <strong style={{ color: theme.color.accentText }}>{requiredTierName}</strong> or above.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            style={{
              border: `1px solid ${theme.color.borderStrong}`,
              background: "transparent",
              color: theme.color.muted,
              borderRadius: theme.radius.sm,
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          <button
            type="button"
            onClick={onRequestUpgrade}
            style={{
              border: "none",
              borderRadius: theme.radius.sm,
              padding: "9px 18px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              background: theme.color.accent,
              color: "#fff",
            }}
          >
            Go to My Services →
          </button>
        </div>
      </div>
    </div>
  );
}
